import { useBelts } from '../../contexts/BeltContext';

/**
 * BeltBadge component displays a visual representation of a martial arts belt
 * with optional stripes. Supports gradient belts for multi-color designs.
 *
 * @param {string} belt - Belt slug (e.g., 'white', 'yellow', 'black_1')
 * @param {number} stripes - Number of stripes (0-4)
 * @param {string} size - Size variant: 'sm', 'md', 'lg'
 * @param {boolean} showLabel - Whether to show the belt name label
 * @param {string} className - Additional CSS classes
 */
const BeltBadge = ({
  belt = 'white',
  stripes = 0,
  size = 'md',
  showLabel = false,
  className = ''
}) => {
  const { getBeltBySlug, getBeltStyles, getBeltName: contextGetBeltName } = useBelts();

  const beltConfig = getBeltBySlug(belt);
  const styles = getBeltStyles(belt);
  const isBlackBelt = beltConfig?.is_black_belt || belt.startsWith('black_');
  const degree = beltConfig?.degree || 0;
  const maxStripes = beltConfig?.max_stripes ?? 4;
  const beltName = beltConfig?.display_name || 'Unknown Belt';

  // Size configurations
  const sizeConfig = {
    sm: {
      belt: { height: '1rem', width: '5rem' },      // h-4 w-20
      stripe: { height: '0.75rem', width: '0.25rem' }, // h-3 w-1
      gap: '2px',
      text: 'text-xs',
    },
    md: {
      belt: { height: '1.5rem', width: '7rem' },    // h-6 w-28
      stripe: { height: '1.25rem', width: '0.375rem' }, // h-5 w-1.5
      gap: '4px',
      text: 'text-sm',
    },
    lg: {
      belt: { height: '2rem', width: '9rem' },      // h-8 w-36
      stripe: { height: '1.75rem', width: '0.5rem' },  // h-7 w-2
      gap: '6px',
      text: 'text-base',
    },
  };

  const config = sizeConfig[size] || sizeConfig.md;

  // Render stripes for colored belts or degree marks for black belts
  const renderMarks = () => {
    const stripeStyle = {
      height: config.stripe.height,
      width: config.stripe.width,
      backgroundColor: styles.stripeColor,
      borderRadius: '2px',
    };

    if (isBlackBelt && degree > 0) {
      // For black belts, show gold/yellow stripes for the degree
      return (
        <div style={{ display: 'flex', gap: config.gap, alignItems: 'center' }}>
          {Array.from({ length: Math.min(degree, 10) }).map((_, i) => (
            <div key={i} style={stripeStyle} />
          ))}
        </div>
      );
    }

    // For colored belts, show black stripes
    if (stripes > 0) {
      return (
        <div style={{ display: 'flex', gap: config.gap, alignItems: 'center' }}>
          {Array.from({ length: Math.min(stripes, maxStripes) }).map((_, i) => (
            <div key={i} style={stripeStyle} />
          ))}
        </div>
      );
    }

    return null;
  };

  // Belt container styles with gradient support
  const beltStyle = {
    height: config.belt.height,
    width: config.belt.width,
    background: styles.background,
    borderColor: styles.borderColor,
    borderWidth: '2px',
    borderStyle: 'solid',
    borderRadius: '2px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingRight: '0.5rem',
    boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
  };

  const labelText = isBlackBelt
    ? beltName
    : stripes > 0
      ? `${beltConfig?.short_name || belt} - ${stripes} Stripe${stripes > 1 ? 's' : ''}`
      : beltName;

  return (
    <div className={`inline-flex flex-col items-center ${className}`}>
      {/* Belt visual */}
      <div
        style={beltStyle}
        title={`${beltName}${stripes > 0 && !isBlackBelt ? ` - ${stripes} stripe${stripes > 1 ? 's' : ''}` : ''}`}
      >
        {renderMarks()}
      </div>

      {/* Label */}
      {showLabel && (
        <span className={`mt-1 ${config.text} text-gray-700 font-medium`}>
          {labelText}
        </span>
      )}
    </div>
  );
};

/**
 * Compact belt indicator for use in lists
 */
export const BeltIndicator = ({ belt = 'white', stripes = 0, className = '' }) => {
  const { getBeltBySlug, getBeltStyles } = useBelts();

  const beltConfig = getBeltBySlug(belt);
  const styles = getBeltStyles(belt);
  const isBlackBelt = beltConfig?.is_black_belt || belt.startsWith('black_');
  const degree = beltConfig?.degree || 0;

  const indicatorStyle = {
    height: '0.75rem',
    width: '2rem',
    background: styles.background,
    borderColor: styles.borderColor,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderRadius: '2px',
  };

  return (
    <div className={`inline-flex items-center gap-1 ${className}`}>
      <div style={indicatorStyle} />
      {!isBlackBelt && stripes > 0 && (
        <span className="text-xs text-gray-500">{stripes}s</span>
      )}
      {isBlackBelt && (
        <span className="text-xs text-gray-500">{degree}D</span>
      )}
    </div>
  );
};

/**
 * Get the display name for a belt
 * This is a wrapper that works with the context
 */
export const getBeltName = (belt, stripes = 0) => {
  // This function is kept for backward compatibility but components
  // should prefer useBelts().getBeltName() for context-aware name lookup
  const defaultNames = {
    white: 'White Belt',
    yellow: 'Yellow Belt',
    orange: 'Orange Belt',
    green: 'Green Belt',
    blue: 'Blue Belt',
    purple: 'Purple Belt',
    brown: 'Brown Belt',
    black_1: '1st Dan Black Belt',
    black_2: '2nd Dan Black Belt',
    black_3: '3rd Dan Black Belt',
    black_4: '4th Dan Black Belt',
    black_5: '5th Dan Black Belt',
    black_6: '6th Dan Black Belt',
    black_7: '7th Dan Black Belt',
    black_8: '8th Dan Black Belt',
    black_9: '9th Dan Black Belt',
    black_10: '10th Dan Black Belt',
  };

  const baseName = defaultNames[belt] || 'Unknown Belt';
  if (belt && belt.startsWith('black_')) {
    return baseName;
  }
  if (stripes > 0) {
    return `${baseName} (${stripes} stripe${stripes > 1 ? 's' : ''})`;
  }
  return baseName;
};

/**
 * Get CSS classes/colors for belt-colored badges/chips
 * Returns color values appropriate for each belt color
 * Note: Returns hex colors instead of Tailwind classes for dynamic belt support
 */
export const getBeltBadgeColors = (belt) => {
  // Default mappings with softer badge colors
  const colorMap = {
    white: { bg: '#F3F4F6', text: '#1F2937', border: '#D1D5DB' },
    yellow: { bg: '#FEF3C7', text: '#92400E', border: '#FCD34D' },
    orange: { bg: '#FFEDD5', text: '#9A3412', border: '#FDBA74' },
    green: { bg: '#DCFCE7', text: '#166534', border: '#86EFAC' },
    blue: { bg: '#DBEAFE', text: '#1E40AF', border: '#93C5FD' },
    purple: { bg: '#F3E8FF', text: '#6B21A8', border: '#C4B5FD' },
    brown: { bg: '#FEF3C7', text: '#78350F', border: '#D97706' },
  };

  // Handle black belts
  if (belt && belt.startsWith('black_')) {
    return { bg: '#1F2937', text: '#FFFFFF', border: '#374151' };
  }

  return colorMap[belt] || colorMap.white;
};

// Legacy exports for backward compatibility
// Components should migrate to using useBelts() hook instead
export const BELT_ORDER = {
  white: 0,
  yellow: 1,
  orange: 2,
  green: 3,
  blue: 4,
  purple: 5,
  brown: 6,
  black_1: 7,
  black_2: 8,
  black_3: 9,
  black_4: 10,
  black_5: 11,
  black_6: 12,
  black_7: 13,
  black_8: 14,
  black_9: 15,
  black_10: 16,
};

export const BELT_OPTIONS = [
  { value: 'white', label: 'White Belt' },
  { value: 'yellow', label: 'Yellow Belt' },
  { value: 'orange', label: 'Orange Belt' },
  { value: 'green', label: 'Green Belt' },
  { value: 'blue', label: 'Blue Belt' },
  { value: 'purple', label: 'Purple Belt' },
  { value: 'brown', label: 'Brown Belt' },
  { value: 'black_1', label: '1st Dan Black Belt' },
  { value: 'black_2', label: '2nd Dan Black Belt' },
  { value: 'black_3', label: '3rd Dan Black Belt' },
  { value: 'black_4', label: '4th Dan Black Belt' },
  { value: 'black_5', label: '5th Dan Black Belt' },
  { value: 'black_6', label: '6th Dan Black Belt' },
  { value: 'black_7', label: '7th Dan Black Belt' },
  { value: 'black_8', label: '8th Dan Black Belt' },
  { value: 'black_9', label: '9th Dan Black Belt' },
  { value: 'black_10', label: '10th Dan Black Belt' },
];

export default BeltBadge;
